import { AsyncPipe } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { ActivatedRoute, RouterLink, RouterLinkActive, RouterOutlet } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { InteractionStatus } from "@azure/msal-browser";
import { NgbCollapse } from "@ng-bootstrap/ng-bootstrap";
import { TranslateDirective, TranslateService } from "@ngx-translate/core";
import { Subject, iif, of } from "rxjs";
import { filter, map, takeUntil, tap } from "rxjs/operators";
import MenuConfig from "./app.menu.config";
import { BusyComponent } from "./common/components/busy/busy.component";
import { EnvironmentRibbonComponent } from "./common/components/environment-ribbon/environment-ribbon.component";
import { QuickSearchComponent } from "./common/components/quick-search/quick-search.component";
import { ToastComponent } from "./common/components/toast/toast.component";
import { HasRoleDirective } from "./common/directives/has-role/has-role.directive";
import { AuthenticationService } from "./common/services/authentication/authentication.service";
import { FeatureService } from "./common/services/feature.service";
import { MenuChild } from "./common/services/menu/models/menu-models";
import { ObjectUtils } from "./common/utils/object-utils";
import { SystemaktivitetNotisComponent } from "./features/administration/systemaktivitet/systemaktivitet-notis/systemaktivitet-notis.component";
import { BreadcrumbNavigationComponent } from "./features/shared/components/breadcrumb-navigation/breadcrumb-navigation.component";
import { DefaultHeaderService } from "./selenium/default-header.service";

@Component({
    selector: "rask-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        EnvironmentRibbonComponent,
        QuickSearchComponent,
        NgbCollapse,
        RouterLink,
        TranslateDirective,
        HasRoleDirective,
        RouterLinkActive,
        SystemaktivitetNotisComponent,
        BreadcrumbNavigationComponent,
        RouterOutlet,
        BusyComponent,
        ToastComponent,
        AsyncPipe
    ]
})
export class AppComponent implements OnInit, OnDestroy {
    protected readonly config = MenuConfig;

    protected isSubmenuCollapsed = true;
    @ViewChild("profileMenuToggle") protected profileMenu: ElementRef;
    protected selectedMenuId?: number;
    protected showProfileMenu = false;

    private readonly _destroying$ = new Subject<void>();
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly cdRef = inject(ChangeDetectorRef);
    private readonly defaultHeaderService = inject(DefaultHeaderService);
    private readonly featureService = inject(FeatureService);
    private readonly msalAuthService = inject(MsalService);
    private readonly msalBroadcastService = inject(MsalBroadcastService);
    private readonly translate = inject(TranslateService);
    private readonly zone = inject(NgZone);

    constructor() {
        this.translate.use("sv-SE");
    }

    protected get isAuthenticated() {
        return this.authenticationService.authenticated();
    }

    protected get isProfileMenuCollapsed() {
        return !this.showProfileMenu;
    }

    checkAndSetActiveAccount() {
        const activeAccount = this.msalAuthService.instance.getActiveAccount();

        if (!activeAccount && this.msalAuthService.instance.getAllAccounts().length > 0) {
            const accounts = this.msalAuthService.instance.getAllAccounts();
            this.msalAuthService.instance.setActiveAccount(accounts[0]);
        }
    }

    clickout = (event: MouseEvent) => {
        if (this.showProfileMenu && !this.profileMenu.nativeElement.contains(event.target)) {
            this.showProfileMenu = false;
            this.cdRef.detectChanges();
        }
    };

    ngOnDestroy() {
        document.removeEventListener("click", this.clickout);
        this._destroying$.next(undefined);
        this._destroying$.complete();
    }

    ngOnInit() {
        this.msalAuthService.handleRedirectObservable().subscribe();
        this.msalBroadcastService.inProgress$
            .pipe(
                filter((status: InteractionStatus) => status === InteractionStatus.None),
                takeUntil(this._destroying$)
            )
            .subscribe(() => {
                this.checkAndSetActiveAccount();
            });
        // om vi hade använt ngbDropdown ist. för collapse hade vi sluppit göra detta manuellt
        // men försökte bevara så mycket som möjligt av original HTML:en
        this.zone.runOutsideAngular(() => {
            document.addEventListener("click", this.clickout);
        });

        this.activatedRoute.queryParams.pipe(
            filter(qp => qp.testcategory),
            tap(qp => this.defaultHeaderService.headers["rask-selenium-test-category"] = qp.testcategory)
        ).subscribe();
    }

    protected logout() {
        this.authenticationService.logout();
    }

    protected selectMenu(id: number) {
        if (this.selectedMenuId === id) {
            this.selectedMenuId = undefined;
            return;
        }

        this.selectedMenuId = id;
    }

    protected showMenu(child: MenuChild) {
        const featureActive$ = this.featureService.getMenuOptionActive(child.showMenu, child.hideMenu);

        const featureActiveOrNoToggle$ = iif(
            () => ObjectUtils.hasValue(child.showMenu) || ObjectUtils.hasValue(child.hideMenu),
            featureActive$,
            of(true));

        return featureActiveOrNoToggle$.pipe(
            filter(isActive => isActive),
            map(() => {
                if (ObjectUtils.isNullOrUndefined(child.requiredRoles)) {
                    return true;
                }
                return this.authenticationService.hasRoles(child.requiredRoles);
            })
        );
    }

    protected toggleProfileMenu() {
        this.showProfileMenu = !this.showProfileMenu;
    }
}
